/**
 * Document Management Module - File Structure View
 * 
 * Displays documents in a folder/file structure with hierarchical organization
 * Supports custom folder creation and organization
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Breadcrumb, BreadcrumbItem } from 'reactstrap';
import {
  FaFolder,
  FaFolderOpen,
  FaFilePdf,
  FaFileWord,
  FaChevronRight,
  FaChevronDown,
  FaUpload,
  FaTrash,
  FaFolderPlus,
  FaSearch,
} from 'react-icons/fa';
import { MdOutlineDescription } from 'react-icons/md';
import { Document, DocumentCategory, Framework, DocumentStatus } from './types';
import documentService from './documentService';
import './DocumentManagement.css';

interface Folder {
  id: string;
  name: string;
  path: string;
  parentId?: string;
  children: (Folder | Document)[];
  expanded: boolean;
  isCustom?: boolean; // Custom folders created by user
  createdAt?: Date;
  createdBy?: string;
}

interface FileStructureProps {
  showHeader?: boolean; // Option to hide header when embedded
  searchTerm?: string; // Search term from parent
  selectedCategory?: DocumentCategory | 'All'; // Category filter from parent
  selectedFramework?: Framework | 'All'; // Framework filter from parent
  selectedStatus?: DocumentStatus | 'All'; // Status filter from parent
}

const FileStructure: React.FC<FileStructureProps> = ({ 
  showHeader = true,
  searchTerm: parentSearchTerm = '',
  selectedCategory: parentCategory = 'All',
  selectedFramework: parentFramework = 'All',
  selectedStatus: parentStatus = 'All',
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [structure, setStructure] = useState<Folder[]>([]);
  const [customFolders, setCustomFolders] = useState<Folder[]>([]);
  const [searchTerm, setSearchTerm] = useState(parentSearchTerm);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [totalFolders, setTotalFolders] = useState(0);
  const [numCustomFolders, setNumCustomFolders] = useState(0);
  
  // Folder creation modal
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [selectedParentFolder, setSelectedParentFolder] = useState<string | null>(null);
  const [creatingFolder, setCreatingFolder] = useState(false);

  useEffect(() => {
    loadTreeStructure();
    loadCustomFolders(); // Load custom folders for the create folder modal
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentCategory, parentFramework, parentStatus, parentSearchTerm]);

  // Update search term when parent changes it
  useEffect(() => {
    setSearchTerm(parentSearchTerm);
  }, [parentSearchTerm]);

  const loadTreeStructure = async () => {
    setLoading(true);
    try {
      const filters: {
        category?: DocumentCategory;
        framework?: Framework;
        status?: DocumentStatus;
        searchTerm?: string;
      } = {};

      if (parentCategory !== 'All') {
        filters.category = parentCategory;
      }
      if (parentFramework !== 'All') {
        filters.framework = parentFramework;
      }
      if (parentStatus !== 'All') {
        filters.status = parentStatus;
      }
      if (parentSearchTerm) {
        filters.searchTerm = parentSearchTerm;
      }

      const treeData = await documentService.getTreeStructure(filters);
      setStructure(treeData.folders as Folder[]);
      setTotalDocuments(treeData.totalDocuments);
      setTotalFolders(treeData.totalFolders);
      setNumCustomFolders(treeData.customFolders);
    } catch (error: any) {
      console.error('Failed to load tree structure:', error);
      toast.error(error.message || 'Failed to load document tree');
      setStructure([]);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomFolders = async () => {
    try {
      const folders = await documentService.getFolders();
      setCustomFolders(folders);
    } catch (error: any) {
      console.error('Failed to load custom folders', error);
      // Don't show error toast for folders, just log it
    }
  };


  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error('Please enter a folder name');
      return;
    }

    setCreatingFolder(true);
    try {
      const folderPath = selectedParentFolder 
        ? `${selectedParentFolder}/${newFolderName.trim()}` 
        : newFolderName.trim();
      
      const newFolder = await documentService.createFolder({
        name: newFolderName.trim(),
        path: folderPath,
        parentId: selectedParentFolder || undefined,
      });

      setCustomFolders([...customFolders, newFolder]);
      toast.success('Folder created successfully!');
      setShowCreateFolderModal(false);
      setNewFolderName('');
      setSelectedParentFolder(null);
      // Reload tree structure to show the new folder
      await loadTreeStructure();
    } catch (error: any) {
      toast.error(error.message || 'Failed to create folder');
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleDeleteFolder = async (folderId: string) => {
    if (window.confirm('Are you sure you want to delete this folder? Documents inside will not be deleted.')) {
      try {
        await documentService.deleteFolder(folderId);
        const updatedFolders = customFolders.filter(f => f.id !== folderId);
        setCustomFolders(updatedFolders);
        toast.success('Folder deleted successfully');
        // Reload tree structure to reflect the deletion
        await loadTreeStructure();
      } catch (error: any) {
        toast.error(error.message || 'Failed to delete folder');
      }
    }
  };

  const getAllFolders = (folders: Folder[]): Folder[] => {
    const all: Folder[] = [];
    folders.forEach(folder => {
      all.push(folder);
      const subFolders = folder.children.filter(c => 'children' in c) as Folder[];
      if (subFolders.length > 0) {
        all.push(...getAllFolders(subFolders));
      }
    });
    return all;
  };

  const toggleFolder = (folderId: string) => {
    const updateFolder = (folders: Folder[]): Folder[] => {
      return folders.map(folder => {
        if (folder.id === folderId) {
          return { ...folder, expanded: !folder.expanded };
        }
        if (folder.children.some(child => 'id' in child && child.id === folderId)) {
          return {
            ...folder,
            children: folder.children.map(child =>
              'children' in child && child.id === folderId
                ? { ...child, expanded: !child.expanded }
                : child
            ) as (Folder | Document)[],
          };
        }
        if (folder.children.some(child => 'children' in child)) {
          const folderChildren = folder.children.filter(child => 'children' in child) as Folder[];
          const documentChildren = folder.children.filter(child => !('children' in child)) as Document[];
          const updatedFolders = updateFolder(folderChildren);
          return {
            ...folder,
            children: [...updatedFolders, ...documentChildren] as (Folder | Document)[],
          };
        }
        return folder;
      });
    };

    setStructure(updateFolder(structure));
  };

  const getFileIcon = (fileName: string) => {
    if (fileName.toLowerCase().endsWith('.pdf')) {
      return <FaFilePdf className="text-danger" size={20} />;
    } else if (fileName.toLowerCase().endsWith('.docx') || fileName.toLowerCase().endsWith('.doc')) {
      return <FaFileWord className="text-primary" size={20} />;
    }
    return <MdOutlineDescription size={20} />;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const renderFolder = (folder: Folder, level: number = 0): React.ReactNode => {
    // Calculate indentation: each level adds 40px
    // Level 0 = 16px, Level 1 = 56px, Level 2 = 96px, etc.
    const indent = level * 40;
    const isExpanded = folder.expanded;
    const isCustom = folder.isCustom || false;
    const folderCount = folder.children.filter(child => 'children' in child).length;
    const documentCount = folder.children.filter(child => !('children' in child)).length;
    // Base padding of 16px + indentation based on level
    const folderPadding = 16 + indent;

    return (
      <div key={folder.id} className="file-structure-item">
        <div
          className={`d-flex align-items-center py-2 px-3 folder-item ${isCustom ? 'custom-folder' : ''}`}
          style={{ paddingLeft: `${folderPadding}px` }}
          onClick={() => toggleFolder(folder.id)}
        >
          <div className="me-2">
            {isExpanded ? (
              <FaChevronDown className="text-muted" size={12} />
            ) : (
              <FaChevronRight className="text-muted" size={12} />
            )}
          </div>
          <div className="me-2">
            {isExpanded ? (
              <FaFolderOpen className={isCustom ? 'text-info' : 'text-warning'} size={22} />
            ) : (
              <FaFolder className={isCustom ? 'text-info' : 'text-warning'} size={22} />
            )}
          </div>
          <div className="flex-grow-1">
            {/* Count badge above folder name */}
            <div className="mb-1">
              <span className="badge bg-secondary" style={{ fontSize: '0.65rem', fontWeight: '600' }}>
                {folderCount > 0 && `${folderCount} folder${folderCount !== 1 ? 's' : ''}`}
                {folderCount > 0 && documentCount > 0 && ' • '}
                {documentCount > 0 && `${documentCount} doc${documentCount !== 1 ? 's' : ''}`}
                {folderCount === 0 && documentCount === 0 && 'Empty'}
              </span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <span className="fw-bold">{folder.name}</span>
              {isCustom && (
                <span className="badge bg-info text-dark" style={{ fontSize: '0.7rem' }}>
                  Custom
                </span>
              )}
            </div>
            {folder.createdAt && (
              <div className="text-muted small mt-1">
                Created {formatDate(folder.createdAt)}
              </div>
            )}
          </div>
          {isCustom && (
            <div className="ms-2 d-flex gap-1" onClick={(e) => e.stopPropagation()}>
              <button
                className="btn btn-sm btn-outline-danger"
                onClick={() => handleDeleteFolder(folder.id)}
                title="Delete folder"
              >
                <FaTrash size={12} />
              </button>
            </div>
          )}
        </div>

        {isExpanded && (
          <div>
            {folder.children.map((item, idx) => {
              if ('children' in item) {
                // It's a folder - increment level for nested folders
                const nextLevel = level + 1;
                return renderFolder(item as Folder, nextLevel);
              } else {
                // It's a document - use current level for indentation
                const doc = item as Document;
                const currentVersion = doc.versions.find(
                  v => v.versionNumber === doc.metadata.currentVersion
                );
                // Files are indented 40px more than their parent folder
                const fileIndent = 16 + indent + 40;
                return (
                  <div
                    key={doc.metadata.id}
                    className="d-flex align-items-center py-2 px-3 file-item"
                    style={{ paddingLeft: `${fileIndent}px` }}
                    onClick={() => navigate(`/documents/${doc.metadata.id}`)}
                  >
                    <div className="me-2">
                      {getFileIcon(currentVersion?.fileName || '')}
                    </div>
                    <div className="flex-grow-1">
                      <div className="fw-semibold">{doc.metadata.title}</div>
                      <div className="text-muted small d-flex gap-3">
                        <span>v{doc.metadata.currentVersion}</span>
                        {currentVersion && (
                          <>
                            <span>{formatFileSize(currentVersion.fileSize)}</span>
                            <span>{formatDate(doc.metadata.updatedAt)}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="ms-2">
                      <span
                        className={`badge ${
                          doc.metadata.status === 'Published' || doc.metadata.status === 'Approved'
                            ? 'bg-success'
                            : doc.metadata.status === 'Under Review'
                            ? 'bg-warning text-dark'
                            : doc.metadata.status === 'Expired'
                            ? 'bg-danger'
                            : 'bg-secondary'
                        }`}
                      >
                        {doc.metadata.status}
                      </span>
                    </div>
                  </div>
                );
              }
            })}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="document-management-container">
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  // Filter structure based on search term (if not already filtered by parent)
  const filteredStructure = searchTerm && !parentSearchTerm
    ? structure.filter(folder => {
        const searchLower = searchTerm.toLowerCase();
        const matchesFolder = folder.name.toLowerCase().includes(searchLower);
        const matchesChildren = folder.children.some(child => {
          if ('children' in child) {
            return (child as Folder).name.toLowerCase().includes(searchLower);
          } else {
            return (child as Document).metadata.title.toLowerCase().includes(searchLower);
          }
        });
        return matchesFolder || matchesChildren;
      })
    : structure;

  return (
    <div className="document-management-container" style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <div className="container-fluid p-4" style={{ paddingTop: showHeader ? '2rem' : '0', display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
        {/* Header - Only show if showHeader is true */}
        {showHeader && (
          <>
            {/* Breadcrumb */}
            <Breadcrumb>
              <BreadcrumbItem>{t('header.you_are_here')}</BreadcrumbItem>
              <span className="mx-2">/</span>
              <BreadcrumbItem active style={{ paddingRight: '10px' }}>
                File Structure
              </BreadcrumbItem>
            </Breadcrumb>

            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">File Structure</h5>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-success d-flex align-items-center gap-2"
                  onClick={() => setShowCreateFolderModal(true)}
                >
                  <FaFolderPlus /> Create Folder
                </button>
                <button
                  className="btn btn-primary d-flex align-items-center gap-2"
                  onClick={() => navigate('/documents/upload')}
                >
                  <FaUpload /> Upload Document
                </button>
              </div>
            </div>
          </>
        )}

        

        {/* Search Bar - Only show if showHeader is true */}
        {showHeader && (
          <div className="card mb-4">
            <div className="card-body">
              <div className="input-group">
                <span className="input-group-text">
                  <FaSearch />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search folders and documents..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setSearchTerm('')}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Summary - Only show if showHeader is true */}
        {showHeader && structure.length > 0 && (
          <div className="card mb-4">
            <div className="card-body">
              <div className="row text-center">
                <div className="col-md-3">
                  <div className="fw-bold text-primary fs-4">{structure.length}</div>
                  <div className="text-muted small">Top Level Folders</div>
                </div>
                <div className="col-md-3">
                  <div className="fw-bold text-info fs-4">
                    {numCustomFolders}
                  </div>
                  <div className="text-muted small">Custom Folders</div>
                </div>
                <div className="col-md-3">
                  <div className="fw-bold text-success fs-4">{totalDocuments}</div>
                  <div className="text-muted small">Total Documents</div>
                </div>
                <div className="col-md-3">
                  <div className="fw-bold text-warning fs-4">
                    {totalFolders}
                  </div>
                  <div className="text-muted small">Total Folders</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* File Structure Tree - Full Page */}
        <div style={{ width: '100%', backgroundColor: 'transparent', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          {/* Toolbar */}
          <div className="d-flex justify-content-between align-items-center mb-3" style={{ flexShrink: 0 }}>
            <h5 className="mb-0 d-flex align-items-center gap-2">
              <FaFolder className="text-warning" />
              Document Structure
            </h5>
            <div className="d-flex gap-2 align-items-center">
              <button
                className="btn btn-sm d-flex align-items-center gap-2"
                onClick={() => setShowCreateFolderModal(true)}
                title="Create a new folder"
                style={{
                  backgroundColor: '#28a745',
                  borderColor: '#28a745',
                  color: '#ffffff',
                  fontWeight: '500',
                  padding: '6px 14px',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 4px rgba(40, 167, 69, 0.2)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#218838';
                  e.currentTarget.style.borderColor = '#218838';
                  e.currentTarget.style.boxShadow = '0 3px 6px rgba(40, 167, 69, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#28a745';
                  e.currentTarget.style.borderColor = '#28a745';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(40, 167, 69, 0.2)';
                }}
              >
                <FaFolderPlus size={14} />
                <span>Create Folder</span>
              </button>
              <div 
                className="d-flex"
                style={{
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px',
                  padding: '2px',
                  border: '1px solid #dee2e6',
                  gap: '2px'
                }}
              >
                <button
                  className="btn btn-sm d-flex align-items-center gap-1"
                  onClick={() => {
                    const expandAll = (folders: Folder[]): Folder[] => {
                      return folders.map(f => ({
                        ...f,
                        expanded: true,
                        children: f.children.map(child =>
                          'children' in child
                            ? { ...(child as Folder), expanded: true }
                            : child
                        ) as (Folder | Document)[],
                      }));
                    };
                    setStructure(expandAll(structure));
                  }}
                  title="Expand All"
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#495057',
                    fontWeight: '500',
                    padding: '6px 12px',
                    borderRadius: '4px',
                    transition: 'all 0.2s ease',
                    fontSize: '0.875rem'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#e9ecef';
                    e.currentTarget.style.color = '#0d6efd';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#495057';
                  }}
                >
                  <FaChevronDown size={12} />
                  <span>Expand All</span>
                </button>
                <button
                  className="btn btn-sm d-flex align-items-center gap-1"
                  onClick={() => {
                    const collapseAll = (folders: Folder[]): Folder[] => {
                      return folders.map(f => ({
                        ...f,
                        expanded: false,
                        children: f.children.map(child =>
                          'children' in child
                            ? { ...(child as Folder), expanded: false }
                            : child
                        ) as (Folder | Document)[],
                      }));
                    };
                    setStructure(collapseAll(structure));
                  }}
                  title="Collapse All"
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#495057',
                    fontWeight: '500',
                    padding: '6px 12px',
                    borderRadius: '4px',
                    transition: 'all 0.2s ease',
                    fontSize: '0.875rem'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#e9ecef';
                    e.currentTarget.style.color = '#0d6efd';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#495057';
                  }}
                >
                  <FaChevronRight size={12} />
                  <span>Collapse All</span>
                </button>
              </div>
            </div>
          </div>

          {/* Tree Content */}
          {filteredStructure.length === 0 ? (
            <div className="text-center p-5" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
              <FaFolder size={64} className="text-muted mb-3 opacity-50" />
              <h5>{searchTerm ? 'No results found' : 'No documents found'}</h5>
              <p className="text-muted mb-3">
                {searchTerm
                  ? 'Try a different search term'
                  : 'Create a folder or upload documents to get started'}
              </p>
              {!searchTerm && (
                <div className="d-flex gap-2 justify-content-center">
                  <button
                    className="btn btn-success"
                    onClick={() => setShowCreateFolderModal(true)}
                  >
                    <FaFolderPlus className="me-2" />
                    Create Folder
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate('/documents/upload')}
                  >
                    <FaUpload className="me-2" />
                    Upload Document
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="file-structure-tree" style={{ backgroundColor: 'transparent', flex: 1, minHeight: 0 }}>
              {filteredStructure.map(folder => renderFolder(folder, 0))}
            </div>
          )}
        </div>

        {/* Create Folder Modal */}
        <Modal isOpen={showCreateFolderModal} toggle={() => setShowCreateFolderModal(false)}>
          <ModalHeader toggle={() => setShowCreateFolderModal(false)}>
            <FaFolderPlus className="me-2" />
            Create New Folder
          </ModalHeader>
          <ModalBody>
            <div className="mb-3">
              <label className="form-label">Folder Name *</label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter folder name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateFolder();
                  }
                }}
                autoFocus
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Parent Folder (Optional)</label>
              <select
                className="form-select"
                value={selectedParentFolder || ''}
                onChange={(e) => setSelectedParentFolder(e.target.value || null)}
              >
                <option value="">Root Level</option>
                {getAllFolders(structure).map(folder => (
                  <option key={folder.id} value={folder.id}>
                    {folder.path}
                  </option>
                ))}
              </select>
              <small className="form-text text-muted">
                Select a parent folder to create a subfolder, or leave empty for root level
              </small>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={() => {
                setShowCreateFolderModal(false);
                setNewFolderName('');
                setSelectedParentFolder(null);
              }}
            >
              Cancel
            </Button>
            <Button
              color="success"
              onClick={handleCreateFolder}
              disabled={creatingFolder || !newFolderName.trim()}
            >
              {creatingFolder ? 'Creating...' : 'Create Folder'}
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </div>
  );
};

export default FileStructure;
